import { z } from 'zod';
import { uuidSchema } from './common.js';
import { safeHttpsUrl, toCents } from './virtual-comp.js';

/**
 * SKU replacement cost DTOs (SIL-1955, phase 1 of SIL-1954).
 *
 * Replacement cost = what it would cost to buy the SKU NEW (or a stated nearest
 * build), as a low-high range with the lines behind it. It is an UPPER LIMIT on
 * used value, not a market comp and not a virtual comp. OPS-ONLY surface: lines
 * carry supplier prices, part numbers and URLs and `notes` is free text. Nothing
 * in FMV reads this yet (phase 2).
 *
 * The caller never sends a price: price_low/high are computed server-side as
 * sum(quantity * unit_price_low/high) in integer cents, and the DB insert trigger
 * re-asserts both sums. A repriced line (e.g. Dell memory $80,940 replaced by
 * market $7,200-$11,200) stays positive: `sourcePriceUsd` holds the OEM number,
 * `unitPrice*Usd` the number actually used.
 */

export const REPLACEMENT_COST_METHODS = ['oem_configurator', 'bom', 'manual'] as const;
export type ReplacementCostMethod = (typeof REPLACEMENT_COST_METHODS)[number];

export const REPLACEMENT_COST_SOURCE_KINDS = [
  'oem_list_price_history',
  'sku_list_price_history',
  'menu_snapshot',
  'dealer_configurator_capture',
  'market_comp',
  'web',
  'manual',
] as const;
export type ReplacementCostSourceKind = (typeof REPLACEMENT_COST_SOURCE_KINDS)[number];

export const REPLACEMENT_COST_SUPERSEDED_REASONS = ['replaced', 'retracted'] as const;

export const REPLACEMENT_COST_MAX_LINES = 100;
export const REPLACEMENT_COST_MAX_QUANTITY = 1_000;
/** Per-line caps keep the sums inside numeric(12,2) with room to spare. */
export const REPLACEMENT_COST_MAX_UNIT_PRICE_USD = 5_000_000;
export const REPLACEMENT_COST_MAX_TOTAL_USD = 100_000_000;
export const REPLACEMENT_COST_ALGORITHM_VERSION = 'replacement_cost_v1';

const partSchema = z.string().trim().min(1).max(200);
const partKeySchema = z.string().trim().min(1).max(200);
const quantitySchema = z.number().int().positive().max(REPLACEMENT_COST_MAX_QUANTITY);
const twoDecimals = (n: number) => Math.abs(Math.round(n * 100) - n * 100) < 1e-6;
const usdSchema = z
  .number()
  .finite()
  .positive()
  .max(REPLACEMENT_COST_MAX_UNIT_PRICE_USD)
  .refine(twoDecimals, 'at most 2 decimals');
const httpsUrlSchema = z
  .string()
  .max(2048)
  .refine((v) => safeHttpsUrl(v) !== null, 'sourceUrl must be a valid https:// URL without credentials');

/** Normalised key for matching a part across SKUs (phase 4): lowercase, alphanumerics and . _ - / + only. */
export function normalizeReplacementPartKey(part: string): string {
  return part
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._\-/+]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const replacementCostLineInputSchema = z
  .object({
    part: partSchema,
    partKey: partKeySchema,
    quantity: quantitySchema,
    unitPriceLowUsd: usdSchema,
    unitPriceHighUsd: usdSchema,
    sourceKind: z.enum(REPLACEMENT_COST_SOURCE_KINDS),
    sourceRef: uuidSchema.nullable().default(null),
    sourceUrl: httpsUrlSchema.nullable().default(null),
    /** The unrepriced source number (e.g. the OEM list line), when it differs from the price used. */
    sourcePriceUsd: usdSchema.nullable().default(null),
    observedAt: z.string().datetime(),
    nearestBuildDelta: z.boolean().default(false),
    deltaNote: z.string().trim().min(1).max(1000).nullable().default(null),
  })
  .strict()
  .refine((l) => l.unitPriceLowUsd <= l.unitPriceHighUsd, {
    path: ['unitPriceHighUsd'],
    message: 'unitPriceHighUsd must be >= unitPriceLowUsd',
  })
  .refine((l) => !l.nearestBuildDelta || (l.deltaNote !== null && l.deltaNote.trim().length > 0), {
    path: ['deltaNote'],
    message: 'deltaNote is required when nearestBuildDelta is true',
  });
export type ReplacementCostLineInput = z.infer<typeof replacementCostLineInputSchema>;

export const createReplacementCostBodySchema = z
  .object({
    method: z.enum(REPLACEMENT_COST_METHODS),
    lines: z.array(replacementCostLineInputSchema).min(1).max(REPLACEMENT_COST_MAX_LINES),
    /** oem_configurator only: the OEM list-price row the nearest build was priced from. */
    baseOemListPriceId: uuidSchema.nullable().default(null),
    observedAt: z.string().datetime(),
    notes: z.string().trim().min(1).max(4000).nullable().default(null),
  })
  .strict();
export type CreateReplacementCostBody = z.infer<typeof createReplacementCostBodySchema>;

export const replacementCostSkuParamSchema = z.object({ skuId: uuidSchema });
export const replacementCostIdParamSchema = z.object({ skuId: uuidSchema, id: uuidSchema });

// ---------------------------------------------------------------------------
// Pure rules shared by the API, the UI and (phase 2) FMV.
// ---------------------------------------------------------------------------

/** low/high totals in USD, computed in integer cents (the DB trigger re-asserts both sums within 0.01). */
export function sumReplacementCostLines(
  lines: ReadonlyArray<{ quantity: number; unitPriceLowUsd: number; unitPriceHighUsd: number }>,
): { lowUsd: number; highUsd: number } {
  let low = 0;
  let high = 0;
  for (const l of lines) {
    low += l.quantity * toCents(l.unitPriceLowUsd);
    high += l.quantity * toCents(l.unitPriceHighUsd);
  }
  return { lowUsd: low / 100, highUsd: high / 100 };
}

const METHOD_RANK: Record<ReplacementCostMethod, number> = { oem_configurator: 0, bom: 1, manual: 2 };

/**
 * The "latest" replacement cost among LIVE rows: newest observedAt, then newest
 * recordedAt, then method precedence oem_configurator > bom > manual. Mirrors
 * v_sku_replacement_cost_latest ORDER BY exactly. Superseded rows are ignored.
 */
export function pickLatestReplacementCost<
  T extends { method: ReplacementCostMethod; observedAt: string; recordedAt: string; supersededAt: string | null },
>(rows: readonly T[]): T | null {
  let best: T | null = null;
  for (const r of rows) {
    if (r.supersededAt !== null) continue;
    if (best === null) {
      best = r;
      continue;
    }
    const byObserved = Date.parse(r.observedAt) - Date.parse(best.observedAt);
    if (byObserved !== 0) {
      if (byObserved > 0) best = r;
      continue;
    }
    const byRecorded = Date.parse(r.recordedAt) - Date.parse(best.recordedAt);
    if (byRecorded !== 0) {
      if (byRecorded > 0) best = r;
      continue;
    }
    if (METHOD_RANK[r.method] < METHOD_RANK[best.method]) best = r;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Response (ops-only). Built field-by-field in the route; never a row spread.
// ---------------------------------------------------------------------------

export const replacementCostLineSchema = z.object({
  part: z.string(),
  partKey: z.string(),
  quantity: z.number(),
  unitPriceLowUsd: z.number(),
  unitPriceHighUsd: z.number(),
  sourceKind: z.enum(REPLACEMENT_COST_SOURCE_KINDS),
  sourceRef: z.string().nullable(),
  /** https-only; null when the stored value fails the render-time check. */
  sourceUrl: z.string().nullable(),
  sourcePriceUsd: z.number().nullable(),
  observedAt: z.string(),
  nearestBuildDelta: z.boolean(),
  deltaNote: z.string().nullable(),
});
export type ReplacementCostLine = z.infer<typeof replacementCostLineSchema>;

export const replacementCostSchema = z.object({
  id: z.string().uuid(),
  skuId: z.string().uuid(),
  method: z.enum(REPLACEMENT_COST_METHODS),
  priceLowUsd: z.number(),
  priceHighUsd: z.number(),
  lines: z.array(replacementCostLineSchema),
  baseOemListPriceId: z.string().uuid().nullable(),
  observedAt: z.string(),
  recordedAt: z.string(),
  recordedByActorSub: z.string(),
  notes: z.string().nullable(),
  supersededAt: z.string().nullable(),
  supersededReason: z.enum(REPLACEMENT_COST_SUPERSEDED_REASONS).nullable(),
});
export type ReplacementCost = z.infer<typeof replacementCostSchema>;

export const replacementCostListResponseSchema = z.object({
  /** Live row per method, newest observation first. */
  liveByMethod: z.array(replacementCostSchema),
  /** The live row that wins the precedence rule (null when none). */
  latest: replacementCostSchema.nullable(),
  /** Superseded rows, newest first. */
  history: z.array(replacementCostSchema),
});
export type ReplacementCostListResponse = z.infer<typeof replacementCostListResponseSchema>;

export const createReplacementCostResponseSchema = z.object({
  replacementCost: replacementCostSchema,
  /** false when an identical live row already existed (idempotent replay). */
  created: z.boolean(),
});

export const retractReplacementCostResponseSchema = z.object({ replacementCost: replacementCostSchema });
